/**
 * PRODUKSJONSADVANCEN — huset ↔ arrangøren, og byrået som ser salen (agent F,
 * `docs/SPILLESTED-KONSERT.md` skjerm 7 og 9, «Salens spesifikasjon i advancen»).
 *
 * Tre lesere av den samme konserten, og hver av dem får sin egen form:
 *
 *   `ConcertAdvance`       arrangøren: salen (fra huset, siste versjon) ved siden av artistens
 *                          tekniske rider (fra byrået), og avvikene under.
 *   `NightProduction`      huset: konsertens kjøreplan, artistens rider og reisefølge (navn og
 *                          roller), og avvikene.
 *   `PerformanceRoom`      byrået: salens spesifikasjon og kjøreplanen på en programpost det er
 *                          part i.
 *
 * INGEN PENGER I NOEN AV DEM. Salen kommer fra `app.room_spec_doc` med fast feltliste (aldri
 * leie); reisefølget til huset er navn og rolle; avvikene har ingen beløp.
 */
import { z } from 'zod';

import { ROOM_SPEC_FIELD_KEYS } from './venues.js';
import {
  concertAdvanceStateSchema,
  concertBillingSchema,
  concertClockTimeSchema,
  concertScheduleItemSchema,
  concertStatusSchema,
} from './org-concerts.js';

const dateOnly = z.iso.date();
/** Postgres skriver øyeblikk med forskyvning (`+02:00`) inne i et jsonb-dokument. */
const instant = z.iso.datetime({ offset: true });

/* ---------------------------------------------------------------------------- salen */

/** Et fast felt kan være tekst, tall eller ja/nei; et eget felt er alltid tekst. */
const specValue = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const roomSpecChangeSchema = z.object({
  specVersion: z.number().int(),
  changedAt: instant,
  /** camelCase for de faste feltene (`pa`, `stageWidthM`), `custom:<id>` for egne felt. */
  changedFields: z.array(z.string()),
  /** Verdiene før og etter per felt, når de er kjent. Et eget felts «før» er ofte ukjent. */
  before: z.record(z.string(), z.unknown()),
  after: z.record(z.string(), z.unknown()),
});
export type RoomSpecChange = z.infer<typeof roomSpecChangeSchema>;

/**
 * SALENS SPESIFIKASJON, ENDRET OG UTVIDET PÅ ARRANGEMENTET (QA16 funn 43, C5). Et lag oppå salens, aldri
 * en kopi: `fields` er bare feltene som er endret her (nøkkelen er salens — et fast felt eller
 * `custom:<id>`), og `extra` er linjer lagt til for dette arrangementet. Salen selv endres ikke.
 */
export const roomSpecLayerSchema = z
  .object({
    fields: z.record(
      z.string().regex(/^(custom:[0-9a-f-]{36}|[a-zA-Z]{1,40})$/u, 'field_key'),
      specValue,
    ),
    extra: z
      .array(
        z.object({
          id: z.uuid(),
          label: z.string().trim().min(1).max(120),
          value: z.string().trim().max(2000),
        }),
      )
      .max(100),
  })
  .meta({ id: 'RoomSpecLayer' });
export type RoomSpecLayer = z.infer<typeof roomSpecLayerSchema>;

/** Det som sendes: `extra` uten id lager en ny linje. */
export const updateRoomSpecLayerRequestSchema = z
  .object({
    fields: z.record(
      z.string().regex(/^(custom:[0-9a-f-]{36}|[a-zA-Z]{1,40})$/u, 'field_key'),
      specValue,
    ),
    extra: z
      .array(
        z.object({
          id: z.uuid().nullish(),
          label: z.string().trim().min(1).max(120),
          value: z.string().trim().max(2000),
        }),
      )
      .max(100),
  })
  .refine((body) => Object.keys(body.fields).length <= 100, { message: 'Too many fields.' })
  .meta({ id: 'UpdateRoomSpecLayerRequest' });
export type UpdateRoomSpecLayerRequest = z.infer<typeof updateRoomSpecLayerRequestSchema>;

export const productionRoomSpecSchema = z
  .object({
    roomId: z.uuid(),
    roomName: z.string(),
    houseName: z.string(),
    venueName: z.string().nullable(),
    city: z.string().nullable(),
    address: z.string().nullable(),
    fields: z.record(z.string(), specValue),
    layouts: z.array(z.object({ name: z.string(), capacity: z.number().int() })),
    customFields: z.array(
      z.object({ id: z.uuid(), key: z.string(), label: z.string(), value: z.string() }),
    ),
    files: z.array(
      z.object({
        id: z.uuid(),
        kind: z.string(),
        title: z.string(),
        url: z.string().nullable(),
        hasFile: z.boolean(),
        updatedAt: instant,
      }),
    ),
    specVersion: z.number().int(),
    specChangedAt: instant.nullable(),
    /** De siste versjonene, nyeste først. */
    changes: z.array(roomSpecChangeSchema),
    /**
     * ARRANGEMENTETS LAG (QA16 funn 43). Utelatt der salen ikke leses gjennom et arrangement. `nightId` og
     * `eventLayerEditable` sier om leseren er huset som eier arrangementet og kan endre laget.
     */
    nightId: z.uuid().nullish(),
    eventLayer: roomSpecLayerSchema.nullish(),
    eventLayerEditable: z.boolean().nullish(),
  })
  .meta({ id: 'ProductionRoomSpec' });
export type ProductionRoomSpec = z.infer<typeof productionRoomSpecSchema>;

/** De faste feltene i fast rekkefølge — den samme lista A viser salen med. */
export const PRODUCTION_SPEC_FIELD_KEYS = ROOM_SPEC_FIELD_KEYS;

/* ---------------------------------------------------------------------------- rideren */

export const productionRiderSchema = z.object({
  /** Leveransene i Teknisk-seksjonen: det rideren ber om, rad for rad. */
  deliveries: z.array(
    z.object({ name: z.string(), specification: z.string().nullable(), status: z.string() }),
  ),
  links: z.array(z.object({ label: z.string(), url: z.string() })),
  documents: z.array(z.object({ id: z.uuid(), filename: z.string(), createdAt: instant })),
});
export type ProductionRider = z.infer<typeof productionRiderSchema>;

/* ---------------------------------------------------------------------------- avvikene */

export const PRODUCTION_DEVIATION_STATUSES = ['open', 'agreed', 'ok', 'reopened'] as const;
export const productionDeviationStatusSchema = z.enum(PRODUCTION_DEVIATION_STATUSES);
export type ProductionDeviationStatus = z.infer<typeof productionDeviationStatusSchema>;

/** Et fast felt (`pa`, `stageWidthM`, `layouts`) eller et eget felt (`custom:<uuid>`). */
export const productionFieldKeySchema = z
  .string()
  .regex(/^(custom:[0-9a-f-]{36}|[a-zA-Z]{1,40})$/u, 'field_key');

export const productionDeviationSchema = z
  .object({
    id: z.uuid(),
    concertId: z.uuid(),
    performanceId: z.uuid().nullable(),
    fieldKey: z.string().nullable(),
    riderSays: z.string().nullable(),
    venueHas: z.string().nullable(),
    agreed: z.string().nullable(),
    status: productionDeviationStatusSchema,
    reopenedAt: instant.nullable(),
    /** En kode (`spec_changed`), aldri en setning. Web skriver «Endret <dato>». */
    reopenedReason: z.string().nullable(),
    sortOrder: z.number().int(),
    updatedAt: instant,
    updatedByName: z.string().nullable(),
  })
  .meta({ id: 'ProductionDeviation' });
export type ProductionDeviation = z.infer<typeof productionDeviationSchema>;

const deviationText = z.string().trim().max(1000).nullish();

export const createProductionDeviationRequestSchema = z
  .object({
    performanceId: z.uuid().nullish(),
    fieldKey: productionFieldKeySchema.nullish(),
    riderSays: deviationText,
    venueHas: deviationText,
    agreed: deviationText,
    status: productionDeviationStatusSchema.optional(),
  })
  .refine((value) => (value.riderSays ?? '') !== '' || (value.venueHas ?? '') !== '', {
    error: 'deviation_text_required',
    path: ['riderSays'],
  })
  .meta({ id: 'CreateProductionDeviationRequest' });
export type CreateProductionDeviationRequest = z.infer<
  typeof createProductionDeviationRequestSchema
>;

export const updateProductionDeviationRequestSchema = z
  .object({
    performanceId: z.uuid().nullish(),
    fieldKey: productionFieldKeySchema.nullish(),
    riderSays: deviationText,
    venueHas: deviationText,
    agreed: deviationText,
    status: productionDeviationStatusSchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { message: 'At least one field.' })
  .meta({ id: 'UpdateProductionDeviationRequest' });
export type UpdateProductionDeviationRequest = z.infer<
  typeof updateProductionDeviationRequestSchema
>;

/* ------------------------------------------------------------------ arrangørens advance */

export const concertAdvancePerformanceSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  billing: concertBillingSchema.nullable(),
  bookingArtistId: z.uuid().nullable(),
  /** Byrået i den delte advancen, eller null (egen artist, eller ikke delt ennå). */
  agencyName: z.string().nullable(),
  rider: productionRiderSchema,
});
export type ConcertAdvancePerformance = z.infer<typeof concertAdvancePerformanceSchema>;

export const concertAdvanceSchema = z
  .object({
    concertId: z.uuid(),
    title: z.string().nullable(),
    startsOn: dateOnly,
    timezone: z.string(),
    venueName: z.string().nullable(),
    roomName: z.string().nullable(),
    /** Versjonen arrangøren sist merket som sett. Null: aldri. */
    seenVersion: z.number().int().nullable(),
    /** Salen, når huset har bekreftet. Null før det — eller når stedet ikke er i Tvitvi. */
    room: productionRoomSpecSchema.nullable(),
    performances: z.array(concertAdvancePerformanceSchema),
    deviations: z.array(productionDeviationSchema),
    /**
     * ARTISTNAVNET SKREVET SOM FRITEKST (sak 239.25): datoforespørselens «Artist» når konserten ikke har
     * en programpost ennå. Null når den har, eller når ingen skrev et navn.
     */
    artistNameText: z.string().nullable().optional(),
  })
  .meta({ id: 'ConcertAdvance' });
export type ConcertAdvance = z.infer<typeof concertAdvanceSchema>;

export const markSpecSeenResponseSchema = z
  .object({ seenVersion: z.number().int() })
  .meta({ id: 'MarkSpecSeenResponse' });
export type MarkSpecSeenResponse = z.infer<typeof markSpecSeenResponseSchema>;

/* ------------------------------------------------------------------------ husets kveld */

export const nightProductionArtistSchema = z.object({
  performanceId: z.uuid(),
  name: z.string(),
  billing: concertBillingSchema.nullable(),
  rider: productionRiderSchema,
  /** Reisefølget: navn og rolle. Ingen telefon, e-post eller allergier — og aldri honorar. */
  party: z.array(z.object({ name: z.string(), role: z.string().nullable() })),
});
export type NightProductionArtist = z.infer<typeof nightProductionArtistSchema>;

export const nightProductionSchema = z
  .object({
    /** Null: kvelden er ikke koblet til en konsert i Tvitvi (lagt inn for hånd). */
    concert: z
      .object({
        id: z.uuid(),
        title: z.string().nullable(),
        organiserName: z.string(),
        startsOn: dateOnly,
        timezone: z.string(),
        doorsAt: instant.nullable(),
        curfewAt: instant.nullable(),
        getInAt: instant.nullable(),
      })
      .nullable(),
    schedule: z.array(concertScheduleItemSchema),
    artists: z.array(nightProductionArtistSchema),
    deviations: z.array(productionDeviationSchema),
    /** Husets egen sal, samme dokument som arrangøren får (0434). Null uten koblet konsert. */
    room: productionRoomSpecSchema.nullable(),
    /** Arrangementets artistnavn skrevet som fritekst (239.25), når ingen opptredende er lagt til. */
    artistNameText: z.string().nullable().optional(),
  })
  .meta({ id: 'NightProduction' });
export type NightProduction = z.infer<typeof nightProductionSchema>;

/* ----------------------------------------------------------------------------- byrået */

export const performanceRoomSchema = z
  .object({
    concertId: z.uuid().nullable(),
    room: productionRoomSpecSchema.nullable(),
    schedule: z.array(concertScheduleItemSchema),
    timezone: z.string().nullable(),
    /**
     * AVVIK OG AVTALER, BARE FOR LESING (sak 222.10, 0444). Avvikene på byråets egen artist og de
     * som gjelder hele konserten — aldri en annen artists. Skriving kommer med treparts-advancen.
     */
    deviations: z.array(productionDeviationSchema),
    /** Artisten på programposten, så «gjelder» kan si hvem framfor «alle artister». */
    artistName: z.string().nullable(),
  })
  .meta({ id: 'PerformanceRoom' });
export type PerformanceRoom = z.infer<typeof performanceRoomSchema>;

/* ----------------------------------------------------------------- byrå som arrangør */

export const createOwnConcertRequestSchema = z
  .object({
    bookingArtistId: z.uuid(),
    bookingArtistFormatId: z.uuid().nullish(),
    startsOn: dateOnly,
    title: z.string().trim().max(160).nullish(),
    venueName: z.string().trim().max(160).nullish(),
    roomName: z.string().trim().max(160).nullish(),
    city: z.string().trim().max(160).nullish(),
    /**
     * Stedet valgt fra Tvitvi, som i «Ny konsert» (QA runde 1, P2-7): en sal sender
     * datoforespørselen til huset, et sted i stedsregisteret lagrer den som tekst. Uten begge er
     * stedet fritekst.
     */
    venueRoomId: z.uuid().nullish(),
    venueOrganizationId: z.uuid().nullish(),
    venueDirectoryRoomId: z.uuid().nullish(),
    doorsAt: concertClockTimeSchema.nullish(),
    curfewAt: concertClockTimeSchema.nullish(),
  })
  .meta({ id: 'CreateOwnConcertRequest' });
export type CreateOwnConcertRequest = z.infer<typeof createOwnConcertRequestSchema>;

export const createOwnConcertResponseSchema = z
  .object({ concertId: z.uuid(), performanceId: z.uuid(), partySize: z.number().int() })
  .meta({ id: 'CreateOwnConcertResponse' });
export type CreateOwnConcertResponse = z.infer<typeof createOwnConcertResponseSchema>;

/** Byråets egne konserter for én artist — raden «Egen konsert» i Bookinger og Kalender. */
export const ownConcertSchema = z
  .object({
    concertId: z.uuid(),
    performanceId: z.uuid(),
    startsOn: dateOnly,
    title: z.string().nullable(),
    venueName: z.string().nullable(),
    roomName: z.string().nullable(),
    city: z.string().nullable(),
    status: concertStatusSchema,
    holdRank: z.number().int().nullable(),
    formatName: z.string().nullable(),
    advanceState: concertAdvanceStateSchema,
    /** Bare når leseren har `concert_budget:read`, og bare når budsjettet kan regnes. */
    breakEvenTickets: z.number().int().nullable(),
    /** Null uten `concert_budget:read`; `false` gir «—» for break-even (QA runde 1, som G-13). */
    hasTicketPrice: z.boolean().nullable(),
  })
  .meta({ id: 'OwnConcert' });
export type OwnConcert = z.infer<typeof ownConcertSchema>;

export const ownConcertListSchema = z
  .object({ items: z.array(ownConcertSchema) })
  .meta({ id: 'OwnConcertList' });
export type OwnConcertList = z.infer<typeof ownConcertListSchema>;

/* ------------------------------------------------------------ konserten i programpostens hode */

/**
 * G-11 (0426): en programpost på en konsert har sted og kveld, ikke scene og klokkeslett. Hodet
 * på programpostsiden viser dette for arrangøren og byrået. Ingen penger.
 */
export const performanceConcertHeadSchema = z
  .object({
    concert: z
      .object({
        concertId: z.uuid(),
        title: z.string().nullable(),
        startsOn: dateOnly,
        timezone: z.string(),
        venueName: z.string().nullable(),
        roomName: z.string().nullable(),
        city: z.string().nullable(),
        /** Huset som eier salen, når det har bekreftet datoen. */
        houseName: z.string().nullable(),
        getInAt: instant.nullable(),
        doorsAt: instant.nullable(),
        curfewAt: instant.nullable(),
      })
      .nullable(),
  })
  .meta({ id: 'PerformanceConcertHead' });
export type PerformanceConcertHead = z.infer<typeof performanceConcertHeadSchema>;
